<script setup lang="ts">
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';

import { applyCommunityBranding, clearCommunityBranding } from '@/branding/runtime';
import HomepageItemPrivateNoAccessDialog from '@/components/dialogs/HomepageItemPrivateNoAccessDialog.vue';
import { useProjectExpansionPage } from '@/composables/useProjectExpansionPage';
import { useCommunityStore } from '@/store/communityStore';
import { useConfigurationStore } from '@/store/configurationStore';
import { themeRegistry } from '@/themes/registry';
import type { ExpansionPageKey } from '@/themes/types';

const props = defineProps<{
  isTemplate: boolean;
  expansionKey: ExpansionPageKey;
}>();

const route = useRoute();
const { t } = useI18n();
const communityStore = useCommunityStore();
const configurationStore = useConfigurationStore();
const homepageItemPrivateNoAccessDialogRef = ref<InstanceType<typeof HomepageItemPrivateNoAccessDialog>>();

const slug = route.params.slug as string;
const moduleId = String(route.params.moduleId ?? '');
const itemId = String(route.params.itemId ?? route.params.newsItemId ?? route.params.eventItemId ?? '');

const {
  project,
  themeKey,
  showNoAccessDialog,
  isLoading,
  userAccess,
  homeLink,
  needLogin,
  expansion,
  canRenderPage,
  loadExpansionPage,
} = useProjectExpansionPage({
  slug,
  isTemplate: props.isTemplate,
  moduleId,
  itemId,
  expansionKey: props.expansionKey,
});

const expansionComponent = computed(() =>
  defineAsyncComponent(themeRegistry.getExpansionPageComponent('project', themeKey.value, props.expansionKey)),
);
const designSystem = computed(() => themeRegistry.getDesignSystem('project', themeKey.value));

watch(
  [() => showNoAccessDialog.value, () => isLoading.value],
  async ([show, loading]) => {
    if (!show || loading) {
      return;
    }

    await nextTick();
    homepageItemPrivateNoAccessDialogRef.value?.openDialog();
  },
  { immediate: true },
);

onMounted(() => {
  loadExpansionPage();
});

watch(
  [() => designSystem.value, () => communityStore.community, () => configurationStore.brandingPresets],
  async ([currentDesignSystem, currentCommunity]) => {
    if (currentDesignSystem !== 'modern') {
      clearCommunityBranding();
      return;
    }

    await applyCommunityBranding(currentCommunity);
  },
  { immediate: true, deep: true },
);

onBeforeUnmount(() => {
  clearCommunityBranding();
});
</script>

<template>
  <div
    v-if="!isLoading"
    class="project-view"
    :class="[`project-view--${designSystem}`, designSystem === 'modern' && 'theme-modern']"
    :data-design-system="designSystem"
  >
    <component
      v-if="canRenderPage"
      :is="expansionComponent"
      :project="project"
      :expansion="expansion"
      :language-enabled="project?.languageEnabled"
    />
    <HomepageItemPrivateNoAccessDialog
      v-if="project && !props.isTemplate && !userAccess.allowed"
      ref="homepageItemPrivateNoAccessDialogRef"
      :home-link="homeLink"
      :need-login="needLogin"
      :item="project"
      :language-enabled="project?.languageEnabled"
    />
  </div>
  <div v-else class="loading">
    {{ t('loading') }}
  </div>
</template>

<style lang="scss" scoped>
.project-view {
  min-height: 100%;
}

.loading {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 50vh;
}
</style>
